/**
 * The frame around the settings sections.
 *
 * The section list is filtered by CAPABILITY, not by role, for the same reason the main
 * navigation is: roles are additive in this product — a solo practitioner holds
 * Organization Administrator, Lawyer, Account Executive and Finance at once — so a
 * role-keyed menu would have to enumerate combinations and would be wrong the first
 * time a firm composed a new one.
 *
 * Filtering here is a courtesy and nothing more. Every one of these screens is behind a
 * server-side capability check, and each will refuse a caller who reaches it by typing
 * the URL.
 */

import { cn } from '@legalcrm/ui';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { PageHeader } from '@/shell/PageHeader';

import { SETTINGS_SECTIONS, type SettingsSection } from './sections';

export { SETTINGS_SECTIONS, type SettingsSection };

export function useVisibleSettingsSections(): SettingsSection[] {
  const { permissions } = useAuth();
  return SETTINGS_SECTIONS.filter((section) => permissions.hasAny(section.requires));
}

export function SettingsLayout() {
  const sections = useVisibleSettingsSections();

  return (
    <>
      <PageHeader title="Settings" description="How this firm is set up." />

      <div className="grid gap-5 lg:grid-cols-[13rem_1fr]">
        <nav aria-label="Settings sections">
          <ul className="flex flex-row flex-wrap gap-1 lg:flex-col">
            {sections.map((section) => (
              <li key={section.to}>
                <NavLink
                  to={section.to}
                  className={({ isActive }) =>
                    cn(
                      'block rounded px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-primary-subtle font-medium text-primary'
                        : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                    )
                  }
                >
                  {section.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </>
  );
}
